import type { ReactElement, ReactNode } from 'react';
import { isNil, path } from 'ramda';
import { Descriptions as AntDescriptions, type DescriptionsProps as AntDescriptionProps, Skeleton } from 'antd';
import type { DescriptionsItemProps } from 'antd/lib/descriptions/Item';
import { toArray } from '@enmon/commons';
import type { FormError } from 'components/form/error';
import { Item as FormItem, type FormItemProps } from 'components/enmon/form/form_ant/Form';
import { VALIDATION_STATUS } from '../form/form_ant/constants';
import { StyledDescriptions, UneditableDescription } from './components/StyledDescriptions';

const { Item: AntItem } = AntDescriptions;

export interface DescriptionsColumn<T = unknown> extends Omit<DescriptionsItemProps, 'label' | 'children' | 'name'> {
  name: string[] | string;
  formItemProps?: Omit<FormItemProps, 'name' | 'children'>;
  editable?: boolean;
  label?: string;
  required?: boolean;
  editContent?: ReactNode;
  errorMessage?: ReactNode;
  /**
   * @deprecated Use `DescriptionsProps.isLoading`
   * instead or remove this deprecation if you figure out that this prop is useful in your case.
   */
  isLoading?: boolean;
  loadingContent?: ReactNode;
  render: (data: T) => ReactNode;
}

export interface DescriptionsProps<T> extends AntDescriptionProps {
  data: T | null | undefined;
  isEditing?: boolean;
  isLoading?: boolean;
  labelWidth?: number;
  columns: readonly DescriptionsColumn<T>[];
  formName?: string;
  formErrors?: readonly FormError[];
}

export { UneditableDescription } from './components/StyledDescriptions';

export const BaseDescriptions = AntDescriptions;

export function Descriptions<T>({
  data,
  isEditing,
  isLoading,
  labelWidth,
  columns,
  formName,
  bordered,
  formErrors,
  ...rest
}: DescriptionsProps<T>): ReactElement {
  return (
    <StyledDescriptions bordered isBordered={bordered ?? false} isEditing={isEditing} labelWidth={labelWidth} {...rest}>
      {columns.map(
        ({
          name,
          label,
          editable,
          required,
          formItemProps,
          editContent,
          errorMessage,
          render,
          isLoading: isColumnLoading,
          loadingContent,
        }) => {
          const joinedName = toArray(name).join('.');
          const getContent = () => {
            if (isLoading || isColumnLoading)
              return loadingContent ?? <Skeleton.Input active block size="small" style={{ width: '65%' }} />;
            // there is not reason to call render if data is null or undefined
            const content = !isNil(data) ? (
              // fallback to <>-</> if render returns null or undefined
              (render(data) ?? <>-</>)
            ) : (
              <>-</>
            );
            return editable ? content : <UneditableDescription>{content}</UneditableDescription>;
          };

          return editable && isEditing ? (
            <AntItem
              key={joinedName}
              label={
                <div className="ant-form-item-label">
                  <label className={required ? 'ant-form-item-required' : ''} htmlFor={`${formName}_${String(name)}`}>
                    {label}
                  </label>
                </div>
              }
            >
              <FormItem
                hasFeedback={errorMessage !== undefined}
                help={errorMessage && errorMessage}
                initialValue={path<unknown>(toArray(name), data)}
                name={name}
                style={{ marginBottom: 0 }}
                validateStatus={errorMessage ? VALIDATION_STATUS.Error : undefined}
                {...formItemProps}
              >
                {editContent}
              </FormItem>
            </AntItem>
          ) : (
            <AntItem key={joinedName} label={<div className="ant-form-item-label">{label}</div>}>
              {getContent()}
            </AntItem>
          );
        },
      )}
    </StyledDescriptions>
  );
}
